'use client'

import { FaqList, FaqQuestion } from '@/lib/apis/sanity'
import { Container } from '@/shared/components/Container'
import Link from 'next/link'
import { PortableText, PortableTextReactComponents } from '@portabletext/react'
import React, { useState } from 'react'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faPlus } from '@fortawesome/pro-regular-svg-icons'

export type Props = {
  content: FaqList
  linkText?: string
}

export default function FaqSectionV2({
  content,
  linkText = 'Visit the FAQs page',
}: Props) {
  return (
    <div className="w-full py-10 sm:py-20">
      <Container className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-4">
        <div className="col-span-1 space-y-2  sm:space-y-4">
          <h2 className="text-left font-moisette text-3xl font-bold text-rich-black md:text-4xl">
            Frequently Asked Questions
          </h2>
          <p className="text-left font-psans text-base text-steel-navy">
            Still have questions?{' '}
            <Link
              href="/faqs"
              className="font-semibold text-blue-600 transition-all hover:underline"
            >
              {linkText}
            </Link>
          </p>
        </div>
        <div className="col-span-1 md:col-span-3">
          <FaqQuestionList id="landing" questions={content.questions} />
        </div>
      </Container>
    </div>
  )
}
type QuestionListProps = {
  id: string
  questions: Array<FaqQuestion>
}

const Components: Partial<PortableTextReactComponents> = {
  list: {
    bullet: ({ children }) => (
      <ul className="my-2.5 max-w-3xl list-disc space-y-2 sm:space-y-4">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="my-2.5 list-decimal space-y-2 sm:space-y-4">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="ml-4 sm:ml-8">{children}</li>,
    number: ({ children }) => <li className="ml-4 sm:ml-8">{children}</li>,
  },

  marks: {
    link: ({ value, children }) => {
      return (
        <a
          className="cursor-pointer font-medium text-blue-600 transition-colors hover:text-blue-500"
          href={value?.href}
          target="_blank"
          rel={'noindex nofollow'}
        >
          {children}
        </a>
      )
    },
  },

  block: {
    normal: ({ children }) => (
      <p className="text-pretty text-left font-psans text-base">{children}</p>
    ),
  },

  hardBreak: false,
}

function FaqQuestionList({ id, questions }: QuestionListProps) {
  return (
    <div className="flex flex-col gap-y-4">
      {questions.map((question, idx) => (
        <Question key={`faq-${id}-${idx}`} question={question} />
      ))}
    </div>
  )
}

type QuestionProps = {
  question: FaqQuestion
}

function Question({ question }: QuestionProps) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div className="flex flex-col gap-y-4 rounded-xl bg-clinical-white p-4">
      <div
        className="flex cursor-pointer items-start justify-between"
        onClick={() => setCollapsed(!collapsed)}
      >
        <h3 className="font-psans text-base font-medium text-rich-black sm:text-lg">
          {question.question}
        </h3>
        <div className="mt-1 text-rich-black">
          <motion.div
            animate={{ rotate: collapsed ? 180 : 0 }}
            transition={{ ease: 'easeOut', duration: 0.2 }}
            aria-hidden={true}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </motion.div>
        </div>
      </div>

      <motion.div
        className={clsx(
          'flex flex-col gap-y-1 text-justify font-psans text-base leading-6 text-steel-navy',
        )}
        initial="closed"
        animate={collapsed ? 'opened' : 'closed'}
        variants={{
          opened: { opacity: 1, height: 'auto', display: 'block' },
          closed: { opacity: 0, height: 0, display: 'none' },
        }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
      >
        <PortableText value={question.answer} components={Components} />
      </motion.div>
    </div>
  )
}
